import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { applyTheme, type ThemeMode, UI_STORE_KEY } from '@core/theme/theme';

export type { ThemeMode };

interface UiStore {
  /** Desktop sidebar expanded (labels visible). Defaults open. */
  sidebarOpen: boolean;
  /** Mobile sheet/drawer open. Independent of desktop expand/collapse. */
  mobileNavOpen: boolean;
  theme: ThemeMode;

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setMobileNavOpen: (open: boolean) => void;
  setTheme: (theme: ThemeMode) => void;
}

export const useUiStore = create<UiStore>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      mobileNavOpen: false,
      // Default to light; a user's saved choice (persisted) still wins.
      theme: 'light',

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: UI_STORE_KEY,
      // Persist desktop expand preference so “open by default” sticks across reloads
      // unless the user explicitly collapsed it.
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) applyTheme(state.theme);
      },
    },
  ),
);
